import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { AUTH, DASHBOARD } from "@/constants/testIds";
import OrdersPanel from "@/pages/panels/OrdersPanel";
import TasksPanel from "@/pages/panels/TasksPanel";
import EmployeesPanel from "@/pages/panels/EmployeesPanel";
import CalendarPanel from "@/pages/panels/CalendarPanel";
import ProductionBoardPanel from "@/pages/panels/ProductionBoardPanel";
import QuickOpenModal from "@/components/QuickOpenModal";
import OrderDetailsModal from "@/components/OrderDetailsModal";
import AlertsCenter from "@/components/AlertsCenter";
import useAlerts from "@/hooks/useAlerts";
import SettingsHub from "@/pages/SettingsHub";
import FinancePanel from "@/pages/panels/FinancePanel";
import FinancialConfigPanel from "@/pages/FinancialConfigPanel";
import ProductTypesPanel from "@/pages/ProductTypesPanel";
import CatalogsHubPanel from "@/pages/CatalogsHubPanel";
import SmartProductsPanel from "@/pages/SmartProductsPanel";
import RolesPanel from "@/pages/RolesPanel";
import RbacAdminPage from "@/rbac_official/RbacAdminPage";
import PricingEnginePanel from "@/pages/PricingEnginePanel";
import UniversalDashboardPanel from "@/pages/panels/UniversalDashboardPanel";
import EstimatesPanel from "@/pages/panels/EstimatesPanel";
import InvoicesPanel from "@/pages/panels/InvoicesPanel";
import CreditNotesPanel from "@/pages/panels/CreditNotesPanel";
import WorkOrdersPanel from "@/pages/panels/WorkOrdersPanel";
import CustomersPanel from "@/pages/panels/CustomersPanel";
import CompanyInfoPanel from "@/pages/CompanyInfoPanel";
// Sprint P1 · Feb 2026 — Product Pricing Catalog (aislado, admin-only).
// Sprint · Feb 2026 — CatalogHub unifica el acceso a Ropa y Uniformes +
// Productos Personalizados desde un solo tab admin.
import CatalogHub from "@/pages/product_catalog/CatalogHub";
import CommissionsPanel from "@/components/CommissionsPanel";

const RAWENS_LOGO =
  "https://customer-assets.emergentagent.com/job_order-production-hub/artifacts/jx48074h_IMG_8811.png";

function KpiCard({ label, value, tone, testId }) {
  const tones = {
    overdue: { bg: "#FF3B30", fg: "#ffffff", accent: "OVERDUE" },
    today: { bg: "#FFCC00", fg: "#0a0a0b", accent: "TODAY" },
    week: { bg: "#FF9500", fg: "#ffffff", accent: "THIS WEEK" },
    total: { bg: "#0a0a0b", fg: "#ffffff", accent: "TOTAL OPEN" },
    pending: { bg: "#0E5FFF", fg: "#ffffff", accent: "PENDING $" },
    collected: { bg: "#1F8A3B", fg: "#ffffff", accent: "COLLECTED $" },
  };
  const s = tones[tone];
  const isMoney = tone === "pending" || tone === "collected";
  const display = isMoney
    ? `$${Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : String(value).padStart(2, "0");
  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden p-6 sm:p-7 flex flex-col justify-between min-h-[160px]"
      style={{ background: s.bg, color: s.fg }}
    >
      <div
        className="absolute inset-0 opacity-[0.08] grid-bg pointer-events-none"
        style={{ backgroundSize: "24px 24px" }}
      />
      <div className="relative">
        <div
          className="font-mono-tech text-[0.65rem] font-bold tracking-[0.25em] opacity-80"
          aria-hidden
        >
          {s.accent}
        </div>
        <div className="font-display text-sm sm:text-base font-bold tracking-tight mt-1 opacity-95">
          {label}
        </div>
      </div>
      <div
        className={`relative font-mono-tech font-black leading-none tabular-nums ${
          isMoney ? "text-3xl sm:text-4xl" : "text-6xl sm:text-7xl"
        }`}
      >
        {display}
      </div>
    </div>
  );
}

// GP 1.0 · Sprint 1 — "production" (Production Queue) queda oculto del menú
// principal. La vista de seguimiento operativa es ahora "orders" (que lista
// Work Orders + órdenes legacy con columnas de producción). El código del
// ProductionBoardPanel se conserva por si se re-habilita.
//
// GP 1.0 · Sprint C — Limpieza de navegación (GP-R004 + GP-R005):
//   • "dashboard" (Tablero) y "insights" (Tablero Universal) quedan
//     OCULTOS del menú principal. Sus rutas, componentes, importaciones y
//     bloques de renderizado se conservan intactos para reactivarlos
//     fácilmente en el futuro (basta con vaciar HIDDEN_TABS).
// "production" oculto del menú (2026-08-11) — módulo intacto; para restaurarlo, reponer "production" en TABS tras "orders"
const TABS = ["dashboard", "insights", "estimates", "invoices", "credit_notes", "work_orders", "orders", "customers", "tasks", "calendar", "employees", "product_catalog", "commissions", "finance", "settings"];
const HIDDEN_TABS = new Set(["dashboard", "insights"]);
const VISIBLE_TABS = TABS.filter((k) => !HIDDEN_TABS.has(k));

// RBAC oficial · tab → permission IDs requeridos (unión OR). Un tab se
// muestra si el principal es admin (legacy o RBAC oficial) o si posee al
// menos uno de los IDs. Tabs sin entrada quedan reservadas para admins.
const TAB_PERMISSIONS = {
  tasks: ["tasks.view_own"],
  customers: ["clients.view_own", "clients.view_any"],
  estimates: ["estimates.view_own", "estimates.view_any"],
  work_orders: ["work_orders.view"],
  // Feb 2026 — bugfix aditivo: Ventas debe poder ver sus propios
  // Invoices y registrar pagos. Reutiliza la matriz de Estimates
  // (misma ownership; el backend usa exactamente esta matriz en
  // `commercial_docs/routes.py::_dep_invoices_view`).
  invoices: ["estimates.view_own", "estimates.view_any"],
  // credit_notes, orders, calendar, employees, settings, dashboard,
  // insights → sólo admin (sin entrada en el mapa oficial).
};

export default function Dashboard() {
  const { user, logout, isAdmin, can, hasOfficialPermission, officialPerms, isPartner } = useAuth();
  const { t, lang, setLang } = useI18n();

  // Determina si un tab debe ser visible para el usuario actual.
  // Fallback aditivo: si el usuario no-admin no tiene ningún rol RBAC
  // oficial (usuario legacy puro), sigue viendo `tasks` como antes.
  const canSeeTab = useCallback(
    (k) => {
      if (isAdmin) return true;
      // Sprint P2 · BUG 2 — Catálogo en modo consulta para empleados.
      // Admin administra, empleado consulta (CatalogHub reusa isAdmin).
      if (k === "product_catalog") return true;
      // Bloque 4 Fase A — tab Comisiones: solo Partner Points (y admin arriba).
      if (k === "commissions") return isPartner;
      const ids = TAB_PERMISSIONS[k];
      if (!ids || !ids.length) return false;
      if (ids.some((pid) => hasOfficialPermission(pid))) return true;
      // Retrocompat: usuario sin roles RBAC oficial ⇒ tasks visible.
      // Bloque 1 Partner Points fix: el fallback aplica SOLO a usuarios
      // legacy puros (sin ningún permiso oficial), como dice el contrato.
      if (k === "tasks" && !((officialPerms?.permissions || []).length)) return true;
      return false;
    },
    [isAdmin, hasOfficialPermission, officialPerms, isPartner]
  );

  const visibleTabsForUser = useMemo(
    () => VISIBLE_TABS.filter((k) => canSeeTab(k)),
    [canSeeTab]
  );

  // GP 1.0 · Sprint C — El landing por defecto para admin era "dashboard"
  // (Tablero), ahora oculto. Se cambia a "estimates" que es la entrada
  // oficial del flujo operativo (Estimate → Invoice → WO → Producción).
  // Para no-admin: primer tab visible según sus permisos RBAC oficial.
  const [tab, setTab] = useState(() => {
    if (isAdmin) return "estimates";
    return "tasks"; // valor provisional; se ajusta al montar según permisos
  });
  // Sub-view within "settings" tab: "hub" | "financial" | "product-types" | "catalogs"
  const [settingsView, setSettingsView] = useState("hub");
  // Fase 7A — flag para abrir Facturas con el filtro "Costo pendiente".
  const [invPendingCost, setInvPendingCost] = useState(false);
  const [orders, setOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [kpis, setKpis] = useState({
    overdue: 0,
    due_today: 0,
    due_week: 0,
    total_open: 0,
    open_orders: 0,
    deliveries_today: 0,
    deliveries_week: 0,
    total_pending: 0,
    total_collected: 0,
  });
  const [reloadKey, setReloadKey] = useState(0);
  // Sprint O2 — Global quick-open palette
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickOrder, setQuickOrder] = useState(null);

  // GP 1.0 · Sprint E — Production Alerts Center (bell + slide-over).
  // Only fetches while a user is logged in; disabled during the auth screen.
  const [alertsOpen, setAlertsOpen] = useState(false);
  const {
    alerts: prodAlerts,
    count: alertsCount,
    loading: alertsLoading,
    refetch: refetchAlerts,
    removeLocal: removeLocalAlert,
  } = useAlerts(Boolean(user));

  // Cmd+K / Ctrl+K / "/" toggle. Ignore "/" when typing in an input/textarea.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const inEditable =
        tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setQuickOpen(true);
      } else if (e.key === "/" && !inEditable) {
        e.preventDefault();
        setQuickOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const loadShared = useCallback(async () => {
    if (!isAdmin) {
      // Employees still need employees list (for assignment names) - but endpoint allows it
      try {
        const [emp] = await Promise.all([axios.get(`${API}/employees`)]);
        setEmployees(emp.data);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      const [ord, emp, kp] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/dashboard/kpis`),
      ]);
      setOrders(ord.data);
      setEmployees(emp.data);
      setKpis(kp.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }, [isAdmin]);

  useEffect(() => {
    loadShared();
  }, [loadShared, reloadKey]);

  // Auto-conmutar al primer tab visible cuando el actual quede fuera de los
  // permisos del usuario (p. ej. tras cargar `hasOfficialPermission`).
  useEffect(() => {
    if (isAdmin) return;
    if (visibleTabsForUser.length === 0) return;
    if (!visibleTabsForUser.includes(tab)) {
      setTab(visibleTabsForUser[0]);
    }
  }, [visibleTabsForUser, tab, isAdmin]);

  // Refresh KPIs every hour (and at midnight) so countdown stays accurate.
  useEffect(() => {
    const interval = setInterval(() => setReloadKey((k) => k + 1), 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleChanged = () => setReloadKey((k) => k + 1);

  const operationalKpis = useMemo(
    () => [
      { key: "overdue", label: t("kpi_overdue"), value: kpis.overdue, tone: "overdue", testId: DASHBOARD.kpiOverdue },
      { key: "today", label: t("kpi_deliveries_today"), value: kpis.deliveries_today, tone: "today", testId: DASHBOARD.kpiDeliveriesToday },
      { key: "week", label: t("kpi_deliveries_week"), value: kpis.deliveries_week, tone: "week", testId: DASHBOARD.kpiDeliveriesWeek },
      { key: "open", label: t("kpi_open_orders"), value: kpis.open_orders, tone: "total", testId: DASHBOARD.kpiOpenOrders },
    ],
    [kpis, t]
  );

  const financialKpis = useMemo(
    () => [
      { key: "pending", label: t("kpi_total_pending"), value: kpis.total_pending, tone: "pending", testId: DASHBOARD.kpiTotalPending },
      { key: "collected", label: t("kpi_total_collected"), value: kpis.total_collected, tone: "collected", testId: DASHBOARD.kpiTotalCollected },
    ],
    [kpis, t]
  );

  return (
    <div data-testid={DASHBOARD.root} className="min-h-screen bg-[var(--bg)]">
      {/* Header */}
      <header className="bg-white border-b border-[var(--border)] sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              data-testid={DASHBOARD.brandLogo}
              src={RAWENS_LOGO}
              alt="Rawen's"
              className="w-10 h-10 object-contain bg-white border border-[var(--border)] p-1"
            />
            <div>
              <div className="font-display font-black text-base sm:text-lg tracking-tight leading-tight">
                {t("app_title")}
              </div>
              <div className="font-mono-tech text-[0.65rem] text-[var(--text-secondary)] tracking-wider">
                {t("welcome")}, {user?.name || user?.identifier} ·{" "}
                {isAdmin ? t("admin").toUpperCase() : t("worker").toUpperCase()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                data-testid={DASHBOARD.quickOpenTrigger}
                onClick={() => setQuickOpen(true)}
                title="Búsqueda rápida (⌘K o /)"
                className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors flex items-center gap-1.5"
              >
                <span>⌕</span>
                <span className="hidden sm:inline">{t("search")} ⌘K</span>
              </button>
            )}
            {/* GP 1.0 · Sprint E — production alerts bell */}
            <button
              data-testid="alerts-bell"
              onClick={() => setAlertsOpen(true)}
              title={t("alert_bell_tooltip")}
              className="relative font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
            >
              <span aria-hidden>🔔</span>
              {alertsCount > 0 && (
                <span
                  data-testid="alerts-bell-badge"
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-mono-tech font-bold leading-[18px] text-center border border-white"
                >
                  {alertsCount > 99 ? "99+" : alertsCount}
                </span>
              )}
            </button>
            <button
              data-testid={AUTH.langToggle}
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
            >
              {lang === "es" ? "ES | en" : "es | EN"}
            </button>
            <button
              data-testid={AUTH.logoutBtn}
              onClick={logout}
              className="font-display font-bold text-sm border border-[var(--border)] px-4 py-1.5 hover:bg-[var(--alert-overdue)] hover:text-white hover:border-[var(--alert-overdue)] transition-colors"
            >
              {t("logout")}
            </button>
          </div>
        </div>

        {/* Tabs — visible para todo usuario autenticado. Los tabs se filtran
            individualmente por RBAC oficial (`canSeeTab`). Los usuarios sin
            permisos operativos siguen viendo el shell vacío (comportamiento
            previo para roles como Producción sin tasks). */}
        <nav className="max-w-[1400px] mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
            {/* GP 1.0 · Sprint C — VISIBLE_TABS excluye "dashboard" y
                "insights" (GP-R004 + GP-R005). Los bloques de render, rutas
                y componentes se conservan más abajo. */}
            {visibleTabsForUser.map((k) => {
              const active = tab === k;
              const ids = {
                dashboard: "tab-dashboard",
                insights: "tab-insights",
                production: DASHBOARD.tabProduction,
                estimates: "tab-estimates",
                invoices: "tab-invoices",
                credit_notes: "tab-credit-notes",
                work_orders: "tab-work-orders",
                orders: DASHBOARD.tabOrders,
                customers: "tab-customers",
                tasks: DASHBOARD.tabTasks,
                calendar: DASHBOARD.tabCalendar,
                employees: DASHBOARD.tabEmployees,
                product_catalog: "tab-product-catalog",
                commissions: "tab-commissions",
                finance: "tab-finance",
                settings: DASHBOARD.tabSettings,
              };
              const labels = {
                dashboard: t("tab_dashboard"),
                insights: t("tab_insights"),
                production: t("tab_production"),
                estimates: t("est_panel_title"),
                invoices: t("inv_panel_title"),
                credit_notes: t("cn_panel_title"),
                work_orders: t("wo_panel_title"),
                orders: t("tab_orders"),
                customers: t("cust_panel_title"),
                tasks: t("tab_tasks"),
                calendar: t("tab_calendar"),
                employees: t("tab_employees"),
                product_catalog: "Catálogo",
                commissions: t("tab_commissions"),
                finance: t("tab_finance"),
                settings: t("tab_settings"),
              };
              return (
                <button
                  key={k}
                  data-testid={ids[k]}
                  onClick={() => {
                    setTab(k);
                    if (k === "settings") setSettingsView("hub");
                  }}
                  className={`px-4 py-3 font-display font-bold text-sm tracking-wide border-b-2 transition-colors whitespace-nowrap ${
                    active
                      ? "border-black text-black"
                      : "border-transparent text-[var(--text-secondary)] hover:text-black"
                  }`}
                >
                  {labels[k]}
                </button>
              );
            })}
          </nav>
      </header>

      {/* Body */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8">
        {isAdmin && tab === "dashboard" && (
          <>
            <section>
              <div className="label-overline mb-1">DSH / 00</div>
              <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
                {t("dashboard_title")}
              </h1>
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                {t("dashboard_subtitle")}
              </p>
            </section>
            <section>
              <div className="label-overline mb-3" style={{ color: "var(--text-secondary)" }}>
                {t("kpi_section_operational")}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-px bg-[var(--border)]">
                {operationalKpis.map((c) => (
                  <KpiCard
                    key={c.key}
                    label={c.label}
                    value={c.value}
                    tone={c.tone}
                    testId={c.testId}
                  />
                ))}
              </div>
            </section>
            {can("financial_dashboard", "view") && (
              <section data-testid="dashboard-financial-section">
                <div className="label-overline mb-3" style={{ color: "var(--accent-cyan, #0E5FFF)" }}>
                  $ · {t("kpi_section_financial")}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--border)]">
                  {financialKpis.map((c) => (
                    <KpiCard
                      key={c.key}
                      label={c.label}
                      value={c.value}
                      tone={c.tone}
                      testId={c.testId}
                    />
                  ))}
                </div>
              </section>
            )}
            <section>
              <OrdersPanel onChanged={handleChanged} compact />
            </section>
          </>
        )}

        {isAdmin && tab === "insights" && (
          <UniversalDashboardPanel
            onNavigate={(drilldown) => {
              const target = drilldown?.panel;
              if (target && TABS.includes(target)) {
                setTab(target);
              }
            }}
          />
        )}

        {isAdmin && tab === "orders" && (
          <OrdersPanel
            onChanged={handleChanged}
            onOpenPricingRule={() => {
              setTab("settings");
              setSettingsView("pricing");
            }}
          />
        )}

        {isAdmin && tab === "production" && (
          <ProductionBoardPanel
            onChanged={handleChanged}
            onOpenPricingRule={() => {
              setTab("settings");
              setSettingsView("pricing");
            }}
          />
        )}

        {tab === "tasks" && (
          <TasksPanel
            employees={employees}
            orders={orders}
            onChanged={handleChanged}
          />
        )}

        {isAdmin && tab === "calendar" && (
          <CalendarPanel
            onOpenPricingRule={() => {
              setTab("settings");
              setSettingsView("pricing");
            }}
          />
        )}

        {isAdmin && tab === "employees" && <EmployeesPanel onChanged={handleChanged} />}

        {/* Sprint · Feb 2026 — Catálogo General: hub con "Ropa y Uniformes"
            (consulta del catálogo existente) y "Productos Personalizados"
            (módulo admin P1). Admin-only. */}
        {tab === "product_catalog" && <CatalogHub />}

        {/* Bloque 4 Fase A — Comisiones Partner Points (admin: todos; partner: suyas). */}
        {tab === "commissions" && (isAdmin || isPartner) && (
          <CommissionsPanel admin={isAdmin} />
        )}

        {isAdmin && tab === "finance" && (
          <FinancePanel
            onReviewPendingCost={() => {
              setInvPendingCost(true);
              setTab("invoices");
            }}
          />
        )}
        {isAdmin && tab === "settings" && settingsView === "hub" && (
          <SettingsHub
            onOpenFinancial={() => setSettingsView("financial")}
            onOpenProductTypes={() => setSettingsView("product-types")}
            onOpenCatalogs={() => setSettingsView("catalogs")}
            onOpenSmartProducts={() => setSettingsView("smart-products")}
            onOpenRoles={() => setSettingsView("roles")}
            onOpenRbacOfficial={() => setSettingsView("rbac-official")}
            onOpenPricing={() => setSettingsView("pricing")}
            onOpenCompanyInfo={() => setSettingsView("company-info")}
          />
        )}
        {isAdmin && tab === "settings" && settingsView === "financial" && (
          <FinancialConfigPanel onBack={() => setSettingsView("hub")} />
        )}
        {isAdmin && tab === "settings" && settingsView === "product-types" && (
          <ProductTypesPanel onBack={() => setSettingsView("hub")} />
        )}
        {isAdmin && tab === "settings" && settingsView === "catalogs" && (
          <CatalogsHubPanel onBack={() => setSettingsView("hub")} />
        )}
        {isAdmin && tab === "settings" && settingsView === "smart-products" && (
          <SmartProductsPanel onBack={() => setSettingsView("hub")} />
        )}
        {isAdmin && tab === "settings" && settingsView === "roles" && (
          <RolesPanel onBack={() => setSettingsView("hub")} />
        )}
        {isAdmin && tab === "settings" && settingsView === "rbac-official" && (
          <RbacAdminPage
            currentUserId={user?.id}
            onBack={() => setSettingsView("hub")}
          />
        )}
        {tab === "estimates" && <EstimatesPanel />}

        {tab === "invoices" && (
          <InvoicesPanel
            initialPendingCost={invPendingCost}
            onPendingCostConsumed={() => setInvPendingCost(false)}
          />
        )}

        {tab === "credit_notes" && <CreditNotesPanel />}
        {isAdmin && tab === "work_orders" && <WorkOrdersPanel />}

        {tab === "customers" && <CustomersPanel />}

        {isAdmin && tab === "settings" && settingsView === "pricing" && (
          <PricingEnginePanel onBack={() => setSettingsView("hub")} />
        )}
        {isAdmin && tab === "settings" && settingsView === "company-info" && (
          <CompanyInfoPanel onBack={() => setSettingsView("hub")} />
        )}
      </main>

      {/* Sprint O2 — Global quick-open palette (Cmd+K / "/" / button) */}
      <QuickOpenModal
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        onOpenOrder={(o) => setQuickOrder(o)}
        onOpenProduct={() => {
          // For now, products land in settings → catalogo (future Sprint O2 polish)
          setTab("settings");
          setSettingsView("financial");
        }}
      />
      {/* GP 1.0 · Sprint E — Production Alerts Center slide-over */}
      <AlertsCenter
        open={alertsOpen}
        onClose={() => setAlertsOpen(false)}
        alerts={prodAlerts}
        loading={alertsLoading}
        onRefetch={refetchAlerts}
        onRemoveLocal={removeLocalAlert}
      />
      {quickOrder && (
        <OrderDetailsModal
          open={!!quickOrder}
          order={quickOrder}
          onClose={() => setQuickOrder(null)}
          employees={employees}
          onChanged={handleChanged}
          onTaskChanged={handleChanged}
          onOpenPricingRule={() => {
            setQuickOrder(null);
            setTab("settings");
            setSettingsView("pricing");
          }}
        />
      )}

      <footer className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 font-mono-tech text-[0.65rem] tracking-wider text-[var(--text-secondary)] flex items-center justify-between">
        <span>PRODUCTION CTRL · v1.0</span>
        <span>{new Date().toISOString().slice(0, 10)}</span>
      </footer>
    </div>
  );
}
